<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useNotificationStore } from '../stores/notificationStore'
import type { TNotificationFilter, TNotificationPriority } from '../types/notification.types'

/**
 * Выбор фильтра перезапрашивает API через setFilter (у каждого фильтра свой набор
 * параметров запроса). Раздел «Приоритет» — фронтовая фильтрация поверх уже
 * загруженных items, без обращения к беку.
 *
 * Фильтры «Избранное» и «Корзина» — фронтовые заглушки: «Избранное» хранится в
 * localStorage, «Корзина» бекендом пока не поддерживается.
 */
interface IFilterItem {
  value: TNotificationFilter
  label: string
  /** Показывать ли бейдж с unreadCount справа от элемента. */
  withBadge?: boolean
}

interface IPriorityItem {
  value: TNotificationPriority
  label: string
  /** Цвет кружка-маркера слева. */
  color: 'error' | 'warning' | 'info'
}

const store = useNotificationStore()
const { filter, priority, unreadCount } = storeToRefs(store)

const filters: IFilterItem[] = [
  { value: 'all', label: 'Все', withBadge: true },
  { value: 'unread', label: 'Непрочитанные' },
  // { value: 'starred', label: 'Избранное' },
  { value: 'adverse_event', label: 'НС' },
  { value: 'ticket', label: 'Заявки' },
  { value: 'calendar_event', label: 'Календарь' },
  // { value: 'trash', label: 'Корзина' },
]

const priorities: IPriorityItem[] = [
  { value: 'critical', label: 'Критичные', color: 'error' },
  { value: 'important', label: 'Важные', color: 'warning' },
  { value: 'info', label: 'Информационные', color: 'info' },
]

function isFilterActive(value: TNotificationFilter): boolean {
  return filter.value === value
}

function isPriorityActive(value: TNotificationPriority): boolean {
  return priority.value === value
}

function selectFilter(value: TNotificationFilter): void {
  store.setFilter(value)
}

function togglePriority(value: TNotificationPriority): void {
  // повторный клик по выбранному — сброс
  store.setPriority(priority.value === value ? null : value)
}

const showAllBadge = computed(() => unreadCount.value > 0)
</script>

<template>
  <div class="notif-sidebar d-flex flex-column ga-2">
    <!-- Список фильтров -->
    <VList
      density="compact"
      nav
      class="notif-sidebar__list"
    >
      <VListItem
        v-for="item in filters"
        :key="item.value"
        :active="isFilterActive(item.value)"
        rounded="lg"
        @click="selectFilter(item.value)"
      >
        <VListItemTitle>{{ item.label }}</VListItemTitle>
        <template
          v-if="item.withBadge && showAllBadge"
          #append
        >
          <VChip
            size="x-small"
            variant="flat"
            color="primary"
            class="notif-sidebar__badge"
          >
            {{ unreadCount }}
          </VChip>
        </template>
      </VListItem>
    </VList>

    <!-- Раздел приоритета -->
    <VList
      density="compact"
      nav
      class="notif-sidebar__list"
    >
      <VListSubheader>Приоритет</VListSubheader>
      <VListItem
        v-for="item in priorities"
        :key="item.value"
        :active="isPriorityActive(item.value)"
        rounded="lg"
        @click="togglePriority(item.value)"
      >
        <template #prepend>
          <span
            class="notif-sidebar__dot"
            :class="`notif-sidebar__dot--${item.color}`"
          />
        </template>
        <VListItemTitle>{{ item.label }}</VListItemTitle>
      </VListItem>
    </VList>
  </div>
</template>

<style scoped lang="scss">
.notif-sidebar {
  padding: 16px 8px;

  &__badge {
    font-weight: 600;
  }

  &__dot {
    inline-size: 12px;
    block-size: 12px;
    margin-inline-end: 12px;
    border-radius: 50%;
    flex-shrink: 0;

    &--error {
      background: rgb(var(--v-theme-error));
    }

    &--warning {
      background: rgb(var(--v-theme-warning));
    }

    &--info {
      background: rgb(var(--v-theme-info));
    }
  }
}
</style>
